import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, Observable, Subject, tap } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { CurrenciesInterface } from 'src/app/shared/interfaces/deposit.interface';
import { AllRateData, RateItem } from 'src/app/shared/interfaces/wallet.interface';
import { CurrencyValuePipe } from 'src/app/shared/pipes/currency-value.pipe';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';

export type ConversionCurrency = {
  currency: CurrenciesInterface | null;
  enable: boolean;
  loading: boolean;
  rate: RateItem[];
};

export type DefaultFiatToFiat = {
  currency: CurrenciesInterface | null;
  rates: RateItem[] | null;
};

export type CurrencyDisplayUpdate = {
  /** 余额顯示转换為「法定货币」的转换类型 */
  inFiatConvertType: string;
  /** 余额顯示是否轉換為「法定货币」 */
  displayInFiat: boolean;
  /** 是否隐藏零数余额 */
  hideZero: boolean;
};

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CurrencyWalletService {
  /** 默认币种 和 其他法币兑换 汇率*/
  defaultToFiat$: BehaviorSubject<DefaultFiatToFiat> = new BehaviorSubject<DefaultFiatToFiat>({
    currency: null,
    rates: [],
  });

  /** 当前显示用于换算的币种 */
  conversionCurrency$: BehaviorSubject<ConversionCurrency> = new BehaviorSubject<ConversionCurrency>({
    currency: null,
    enable: false,
    loading: false,
    rate: [],
  });

  /** 余额顯示转换為「法定货币」的转换类型 */
  inFiatConvertType$: BehaviorSubject<string>;

  /** 余额顯示是否轉換為「法定货币」 */
  displayInFiat$: BehaviorSubject<boolean>;

  /** 是否隐藏零数余额 */
  hideZero$: BehaviorSubject<boolean>;

  /** 是否有可見法幣（無則不提供「Display in」法幣換算） */
  readonly hasVisibleFiat$: Observable<boolean>;

  /** 币种显示狀態更新通知 */
  private readonly _currencyDisplayUpdate$ = new Subject<void>();
  /** 币种显示狀態更新通知 */
  readonly currencyDisplayUpdate$: Observable<CurrencyDisplayUpdate>;

  constructor(
    private localStorageService: LocalStorageService,
    private appService: AppService,
    private userAssetsService: UserAssetsService,
    private currencyValue: CurrencyValuePipe,
  ) {
    const { inFiatConvertType, displayInFiat, hideZero } = this.localStorageService.currencyDisplay || {};
    this.inFiatConvertType$ = new BehaviorSubject(inFiatConvertType);
    this.inFiatConvertType$.pipe(untilDestroyed(this)).subscribe(() => this._currencyDisplayUpdate$.next());
    this.displayInFiat$ = new BehaviorSubject(!!displayInFiat);
    this.displayInFiat$.pipe(untilDestroyed(this)).subscribe(() => this._currencyDisplayUpdate$.next());
    this.hideZero$ = new BehaviorSubject(!!hideZero);
    this.hideZero$.pipe(untilDestroyed(this)).subscribe(() => this._currencyDisplayUpdate$.next());
    this.currencyDisplayUpdate$ = this._currencyDisplayUpdate$.asObservable().pipe(
      debounceTime(20),
      map(() => ({
        inFiatConvertType: this.inFiatConvertType$.value,
        displayInFiat: this.displayInFiat$.value,
        hideZero: this.hideZero$.value,
      })),
      tap(x => (this.localStorageService.currencyDisplay = x)),
    );
    this.hasVisibleFiat$ = this.appService.currencies$.pipe(
      map(currencies => currencies.some(c => c.isVisible && !c.isDigital)),
      distinctUntilChanged(),
    );
    // 幣種清單更新時（app 初始化、KYC 後重抓等）校正法幣顯示設定；略過尚未載入的初始空陣列
    this.appService.currencies$
      .pipe(
        filter(currencies => currencies.length > 0),
        untilDestroyed(this),
      )
      .subscribe(currencies => this.syncFiatDisplay(currencies));
  }

  /**
   * 依幣種清單校正法幣顯示設定：換算幣種無效時改用第一個可見法幣；無可見法幣時關閉法幣顯示
   *
   * @param currencies 幣種清單
   */
  private syncFiatDisplay(currencies: CurrenciesInterface[]): void {
    const visibleFiats = currencies.filter(c => c.isVisible && !c.isDigital);
    if (visibleFiats.length === 0) {
      if (this.displayInFiat$.value) this.displayInFiat$.next(false);
      return;
    }
    if (!visibleFiats.some(c => c.currency === this.inFiatConvertType$.value)) {
      this.inFiatConvertType$.next(visibleFiats[0].currency);
    }
  }

  updateRate(data: AllRateData) {
    const self = this.conversionCurrency$.value;
    if (!self || !self.currency || self.loading) return;
    this.conversionCurrency$.next({ ...self, loading: true });
    const baseCurrency = self.currency.currency;
    const res = this.userAssetsService.getRatesBaseCurrency(data, baseCurrency);
    this.conversionCurrency$.next({
      ...self,
      loading: false,
      rate: res.rates || [],
    });
  }

  updateConversionCurrency(): void {
    const currencies = this.appService.currencies$.value;
    this.conversionCurrency$.next({
      currency: currencies?.find(cur => cur.currency === this.inFiatConvertType$.value) || null,
      enable: this.displayInFiat$.value,
      loading: true,
      rate: this.userAssetsService.getRatesBaseCurrency(
        this.userAssetsService.allRate.value,
        this.inFiatConvertType$.value,
      ).rates,
    });
  }

  amountAfterCalculateRate(balance: number, currency: string) {
    const rate = this.conversionCurrency$.value.rate?.find(x => x.currency === currency)?.rate || 1;
    if (rate) return Number(balance ?? 0) * rate;
    return 0;
  }

  /**金額匯兌（不區分數位貨幣或法定貨幣） */
  convertCurrency(amount: number | null, fromCurrency: string, toCurrency: string): number | null {
    if (fromCurrency === toCurrency) return amount;

    const allRates = this.userAssetsService.allRate.value;
    const referenceRate = this.userAssetsService.getRatesBaseCurrency(allRates, toCurrency).rates;
    const exchangeRate = referenceRate?.find(x => x.currency === fromCurrency)?.rate;
    if (exchangeRate === undefined) return null;

    return Number(amount ?? 0) * exchangeRate;
  }

  balanceFormat(
    balance: number,
    currency: string,
    { hasSymbol = false, hasCurrency = false }: { hasSymbol?: boolean; hasCurrency?: boolean } = {},
  ): string {
    const currencyInfoList = this.appService.currencies$.getValue();
    const fiatCurrencyInfo = currencyInfoList.find(i => i.currency === this.inFiatConvertType$.value);
    // 法幣換算幣種無效（如商戶無可見法幣）時，退回原幣種顯示
    const isDisplayInFiat = this.displayInFiat$.getValue() && !!fiatCurrencyInfo;
    const targetCurrency = isDisplayInFiat ? this.inFiatConvertType$.value : currency;
    let targetBalance = balance;
    if (isDisplayInFiat) targetBalance = this.amountAfterCalculateRate(balance, currency);

    const targetCurrencyInfo = isDisplayInFiat
      ? fiatCurrencyInfo
      : currencyInfoList.find(i => i.currency === targetCurrency);
    const prefix = hasSymbol ? targetCurrencyInfo?.symbol || 'Unknown symbol' : '';
    const suffix = hasCurrency ? ` ${targetCurrencyInfo?.currency || 'Unknown currency'}` : '';
    return prefix + this.currencyValue.transform(targetBalance, targetCurrency) + suffix;
  }
}
